import TemplatesApi from './Templates.api';

const BYTES_PER_MB = 1048576;

export function getMaxVideoUploadSizeInMb(maxVideoUploadSizeBytes) {
  return Math.floor(maxVideoUploadSizeBytes / BYTES_PER_MB);
}

export function isEditableVideoSceneTooLarge(fileSize, maxVideoUploadSizeBytes) {
  return fileSize > maxVideoUploadSizeBytes;
}

export function getEditableVideoSceneSizeLimitMessage(maxVideoUploadSizeBytes) {
  const maxMb = getMaxVideoUploadSizeInMb(maxVideoUploadSizeBytes);
  return `Video templates must be smaller than ${maxMb} MB.`;
}

export function isEditableVideoSceneSizeError(error) {
  const serverMessage = error?.response?.data?.data;
  return typeof serverMessage === 'string' && /too large/i.test(serverMessage);
}

export function readEditableVideoSceneMetadata(cesdk) {
  const pages = cesdk?.engine?.scene?.getPages?.() ?? [];

  if (!pages.length) {
    return null;
  }

  const page = pages[0];
  const { engine } = cesdk;
  const width = Math.round(engine.block.getWidth(page));
  const height = Math.round(engine.block.getHeight(page));
  const durationSeconds = engine.block.getDuration(page);

  if (
    !Number.isFinite(width) ||
    !Number.isFinite(height) ||
    !Number.isFinite(durationSeconds) ||
    width <= 0 ||
    height <= 0 ||
    durationSeconds < 0
  ) {
    return null;
  }

  return {
    width,
    height,
    duration_seconds: durationSeconds,
  };
}

export function formatVideoDuration(durationSeconds) {
  if (!Number.isFinite(durationSeconds) || durationSeconds < 0) {
    return null;
  }

  const totalSeconds = Math.round(durationSeconds);
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;

  return `${minutes}:${String(seconds).padStart(2, '0')}`;
}

export function formatEditableVideoMetadataLabel(metadata) {
  if (!metadata?.width || !metadata?.height) {
    return null;
  }

  const resolution = `${metadata.width} x ${metadata.height}`;
  const duration = formatVideoDuration(metadata.duration_seconds);

  return duration ? `${resolution} • ${duration}` : resolution;
}

export async function loadEditableVideoScene(cesdk, sceneUrl) {
  const archiveBuffer = await TemplatesApi.fetchEditableVideoSceneArchive(
    sceneUrl
  );
  const blob = new Blob([archiveBuffer], { type: 'application/zip' });
  const blobUrl = URL.createObjectURL(blob);

  try {
    await cesdk.engine.scene.loadFromArchiveURL(blobUrl);
    await cesdk.actions.run('zoom.toPage', { page: 'first' });
  } finally {
    URL.revokeObjectURL(blobUrl);
  }
}
